<template>
  <b-container fluid class="container-internal">
    <b-modal
      id="cardSetupModal"
      ref="cardSetupModal"
      title="Add a new card to your account"
      size="md"
      busy
      centered
      ok-only
      no-stacking
      static
    >
      <template v-slot:modal-footer>
        <div
          style="text-center; flex-grow: 1; flex-orientation: columns; text-align: center"
        >
          <b-button
            variant="outline-primary"
            size="xl"
            @click="startCardSetup()"
            block
          >
            <BIconThreeDots animation="cylon" v-if="processing" />
            <BIconCheckCircle animation="throb" v-if="cardSetupSuccessful" />
            <span v-if="!processing && !cardSetupSuccessful"
              >Save card</span
            ></b-button
          >
          <b-toast
            id="cardSetupErrorBox"
            ref="cardSetupErrorBox"
            variant="danger"
            title=""
            static
            solid
            no-close-button
            no-auto-hide
          >
            {{ cardSetupErrorMessage }}
          </b-toast>
        </div>
      </template>
      <template v-slot:default>
        <img
          class="img-payment"
          src="@/assets/images/checkout-credit-cards.png"
        />
        <b-input-group class="paymentInput" label="Debit/Credit">
          <b-input-group-prepend is-text>
            <span class="card-label">
              <CreditCardIcon />
            </span>
          </b-input-group-prepend>
          <div
            id="card-number-element"
            class="form-control stripe-element-container"
          >
            <!-- Stripe Card Element -->
          </div>
        </b-input-group>
        <div ref="cardErrors" role="alert" class="sr-field-error"></div>

        <div class="twoColumn">
          <!-- Payment Validate Input -->
          <div>
            <b-input-group class="paymentInput">
              <b-input-group-prepend is-text>
                <span class="card-label">
                  <CreditCardIcon />
                </span>
              </b-input-group-prepend>
              <div
                id="card-expiry-element"
                class="form-control stripe-element-container"
              >
                <!-- Stripe Card Expiry Element -->
              </div>
            </b-input-group>
            <div
              ref="cardExpiryErrors"
              role="alert"
              class="sr-field-error"
            ></div>
          </div>
          <div>
            <!-- Payment CVC Input -->
            <b-input-group class="paymentInput">
              <b-input-group-prepend is-text>
                <span class="card-label">
                  <CreditCardIcon />
                </span>
              </b-input-group-prepend>
              <div
                id="card-cvc-element"
                class="form-control stripe-element-container"
              >
                <!-- Stripe CVC Element -->
              </div>
            </b-input-group>
            <div ref="cardCvcErrors" role="alert" class="sr-field-error"></div>
          </div>
        </div>

        <b-input-group class="paymentInput">
          <b-input-group-prepend is-text>
            <span class="card-label">
              <CreditCardIcon />
            </span>
          </b-input-group-prepend>
          <div
            id="card-postalCode-element"
            class="form-control stripe-element-container"
          >
            <!-- Stripe Postal Code Element -->
          </div>
        </b-input-group>
        <div
          ref="cardPostalCodeErrors"
          role="alert"
          class="sr-field-error"
        ></div>
      </template>
    </b-modal>

    <div>
      <h2>Your billing details</h2>
    </div>
    <b-row align-h="center" style="margin-top: 1rem">
      <b-col sm="12" md="12" lg="10" align-self="start">
        <b-card class="text-left" header-tag="header" small>
          <template #header>
            <b-icon-credit-card /><strong> Payment Methods</strong
            ><span
              ><b-button
                variant="outline-primary"
                size="sm"
                class="small-button"
                @click="showCardSetupModal()"
                >Add new...</b-button
              ></span
            >
          </template>
          <b-table-simple
            hover
            small
            caption-top
            stacked="sm"
            table-variant="outlined"
            class="payment-method-table"
          >
            <b-thead head-variant="light">
              <b-tr>
                <b-th>Type</b-th>
                <b-th>Brand</b-th>
                <b-th>Number</b-th>
                <b-th>Expires on</b-th>
                <b-th>Country</b-th>
                <b-th colspan="2"></b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="card in cards" :key="card.id">
                <b-td><CreditCardIcon /> Card</b-td>
                <b-td
                  ><b-img :src="brandLogo(card.brand)" width="32"></b-img
                ></b-td>
                <b-td>***-{{ card.last4 }}</b-td>
                <b-td>{{ card.expirationDate }}</b-td>
                <b-td>{{ card.country }}</b-td>
                <b-td>
                  <b-badge
                    href="#"
                    pill
                    variant="success"
                    v-if="card.isDefault"
                    style="margin-right: 0.5rem"
                    >preferred</b-badge
                  >
                  <b-badge
                    href="#"
                    pill
                    variant="light"
                    v-if="!card.isDefault"
                    style="margin-right: 0.5rem; cursor: pointer"
                    @click="setPreferred(card)"
                    >set as preferred</b-badge
                  >
                  <b-badge
                    pill
                    variant="warning"
                    v-if="isExpired(card)"
                    style="margin-right: 0.5rem"
                    >expired</b-badge
                  >
                  <b-badge
                    href="#"
                    pill
                    variant="danger"
                    v-if="!card.isDefault"
                    class="badge-button"
                    style="cursor: pointer"
                    @click="deleteCard(card)"
                    >remove</b-badge
                  >
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-card>
      </b-col>
    </b-row>
    <b-row align-h="center" style="margin-top: 1rem">
      <b-col sm="12" md="12" lg="10" align-self="start">
        <b-card class="text-left" size="sm" header-tag="header">
          <template #header>
            <b-icon-card-heading /> <strong>Billing Information</strong>
          </template>
          <p>
            <em>
              We use the information below to generate the invoices for your
              account.
            </em>
          </p>
          <p>
            <em>
              The fields marked with * are mandatory.
            </em>
          </p>
          <b-form @submit="submitBillingInfo()" style="margin: 1rem; text-left">
            <b-form-group
              label="* Name:"
              label-for="name"
              label-cols="4"
              label-size="sm"
            >
              <b-form-input
                id="name"
                ref="name"
                v-model="$v.form.name.$model"
                name="name"
                class="TextInput"
                size="sm"
                :state="validateInput('name')"
                aria-describedby="name-feedback"
              ></b-form-input>
              <b-form-invalid-feedback id="name-feedback" v-bind:tooltip="false"
                >This is a required field.</b-form-invalid-feedback
              >
            </b-form-group>

            <b-form-group
              label="Company:"
              label-for="company"
              label-cols="4"
              label-size="sm"
            >
              <b-form-input
                id="company"
                ref="company"
                v-model="$v.form.company.$model"
                name="company"
                class="TextInput"
                size="sm"
                :state="validateInput('company')"
                aria-describedby="name-feedback"
              ></b-form-input>
              <b-form-invalid-feedback
                id="company-feedback"
                v-bind:tooltip="false"
                >This is a required field.</b-form-invalid-feedback
              >
            </b-form-group>

            <b-form-group
              label="* Email:"
              label-for="email"
              label-cols="4"
              label-size="sm"
            >
              <b-form-input
                id="email"
                ref="email"
                v-model="$v.form.email.$model"
                name="email"
                class="TextInput"
                size="sm"
                :state="validateInput('email')"
                type="email"
                aria-describedby="email-feedback"
              ></b-form-input>
              <b-form-invalid-feedback
                id="email-feedback"
                v-bind:tooltip="false"
                >This is a required field.</b-form-invalid-feedback
              >
            </b-form-group>
            <b-form-group
              label="* Address Line 1:"
              label-cols="4"
              label-for="address1"
              label-size="sm"
            >
              <b-form-input
                id="address1"
                ref="address1"
                v-model="$v.form.address1.$model"
                name="address1"
                class="TextInput"
                size="sm"
                :state="validateInput('address1')"
                aria-describedby="address1-feedback"
              ></b-form-input>
              <b-form-invalid-feedback
                id="address1-feedback"
                v-bind:tooltip="false"
                >This is a required field.</b-form-invalid-feedback
              >
            </b-form-group>
            <b-form-group
              label="Address Line 2:"
              label-for="address2"
              label-cols="4"
              label-size="sm"
            >
              <b-form-input
                id="address2"
                ref="address2"
                v-model="$v.form.address2.$model"
                name="address2"
                class="TextInput"
                size="sm"
                :state="validateInput('address2')"
                aria-describedby="address2-feedback"
              ></b-form-input>
              <b-form-invalid-feedback
                id="address2-feedback"
                v-bind:tooltip="false"
                >This is a required field.</b-form-invalid-feedback
              >
            </b-form-group>

            <b-form-group
              label="City:"
              label-for="city"
              label-cols="4"
              label-size="sm"
            >
              <b-form-input
                id="city"
                ref="city"
                v-model="$v.form.city.$model"
                name="city"
                class="TextInput"
                size="sm"
                :state="validateInput('city')"
                aria-describedby="name-feedback"
              ></b-form-input>
              <b-form-invalid-feedback id="city-feedback" v-bind:tooltip="false"
                >This is a required field.</b-form-invalid-feedback
              >
            </b-form-group>

            <b-form-group
              label="ZIP Code:"
              label-for="zip"
              label-cols="4"
              label-size="sm"
            >
              <b-form-input
                id="zip"
                ref="zip"
                v-model="$v.form.zip.$model"
                name="zip"
                class="TextInput"
                size="sm"
                :state="validateInput('zip')"
                aria-describedby="zip-feedback"
              ></b-form-input>
              <b-form-invalid-feedback id="zip-feedback" v-bind:tooltip="false"
                >This is a required field.</b-form-invalid-feedback
              >
            </b-form-group>
            <b-form-group
              label="* Country:"
              label-for="country"
              label-cols="4"
              label-size="sm"
            >
              <b-form-select :options="countryOptions" v-model="form.country">
              </b-form-select>
            </b-form-group>
            <b-form-group
              label="VAT number:"
              label-for="vat"
              label-cols="4"
              label-size="sm"
            >
              <b-form-input
                id="taxNumber"
                ref="taxNumber"
                v-model="$v.form.taxNumber.$model"
                name="taxNumber"
                class="TextInput"
                size="sm"
                :state="validateInput('taxNumber')"
                aria-describedby="taxNumber-feedback"
              ></b-form-input>
              <b-form-invalid-feedback
                id="taxNumber-feedback"
                v-bind:tooltip="false"
                >This is a required field.</b-form-invalid-feedback
              >
            </b-form-group>
            <b-form-group class="text-right">
              <b-button
                size="sm"
                variant="outline-secondary"
                style="padding: 0.25rem 1.5rem"
                @click="submitBillingInfo()"
                >Save</b-button
              ><b-toast
                id="success-toast"
                static
                no-auto-hide
                no-close-button
                no-hover-pause
                style="text-align: center; font-size: 1.2em"
              >
                <b-icon-check2-all
                  scale="1"
                  variant="success"
                ></b-icon-check2-all>
                Billing info saved successfully!
              </b-toast>
              <b-toast
                id="failure-toast"
                static
                no-auto-hide
                style="text-align:center; font-size: 1.2em;">
                  {{billingInfoError}}
              </b-toast>
            </b-form-group>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { validationMixin } from "vuelidate";

import { loadStripeSdk } from "vue-stripe-checkout/src/load-stripe-sdk";

import "vue-select/src/scss/vue-select.scss";

import {
  required,
  requiredIf,
  minLength,
  email,
} from "vuelidate/lib/validators";

import { DateTime } from "luxon";

import CreditCardIcon from "@/components/icons/CreditCardIcon.vue";
import {
  BIconCheck2All,
  BIconCheckCircle,
  BIconThreeDots,
} from "bootstrap-vue";

import { BACKEND_URL, STRIPE } from "@/config";

import { countries } from "@/utils/countries";

export default {
  mixins: validationMixin,
  name: "Billing",
  props: ["customerInfo"],
  components: {
    CreditCardIcon,
    BIconCheck2All,
    BIconThreeDots,
    BIconCheckCircle,
  },
  computed: {
    validForm: function () {
      return !this.$v.form.$anyError;
    },
    cardSetupErrorMessage: function () {
      if (!this.cardSetupError) {
        return null;
      }

      console.log(JSON.stringify(this.cardSetupError));

      if (this.cardSetupError.code == "payment_intent_authentication_failure") {
        return "Your account is ready, but your card issuer requires a 3D Secure authentication. Please, Log In using the button on the top of this form and try again. Make sure to confirm the code your bank sent you (through SMS, email, mobile app, etc).";
      }

      if (this.cardSetupError.code != "card_declined") {
        return this.cardSetupError.message;
      }

      switch (this.cardSetupError.decline_code) {
        case "insufficient_funds":
          return "Your account was created and it is ready, but your payment was declined due to insufficient funds. Please, Log In using the button on the top of this form and try with a different card.";
        case "authentication_required":
          return "Your account was created and it is ready, but your card issuer requires a 3D Secure authentication. Please, Log In using the button on the top of this form and try again. Make sure to confirm the code your bank sent you (through SMS, email, mobile app, etc).";
        case "generic_decline":
        case "do_not_honor":
          return "Your account was created and it is ready, but your payment was declined by your card issuer. Please, contact your bank for more information, or Log In using the button on the top of this form and try again with a different card.";
        default:
          return "Your account was created and it is ready, but your payment was declined due to an unknown reason. Please, Log In using the button on the top of this form and try with a different card.";
      }
    },
  },
  created() {
    loadStripeSdk("v3", () => {
      const options = {
        stripeAccount: this.stripeAccount,
        apiVersion: this.apiVersion,
        locale: this.locale,
      };
      if (!this.stripe) {
        this.stripe = window.Stripe(this.stripeKey, options);
        this.createAndMountStripeElements();
      }
    });
  },
  mounted() {
    this.fetchCustomerCards();
    this.fetchCustomerBillingInfo();
  },
  data() {
    return {
      stripeKey: STRIPE.STRIPE_PUB_KEY,
      cards: [],
      invoiceDetails: null,
      countryOptions: countries,
      processing: false,
      cardSetupSuccessful: false,
      form: {
        id: null,
        name: this.customerInfo.user.firstName,
        company: null,
        email: this.customerInfo.user.email,
        address1: null,
        address2: null,
        city: null,
        country: this.customerInfo.user.country,
        zip: null,
        taxNumber: null,
      },
      billingInfoError: null,
      stripe: null,
      cardSetupError: null,
      cardNumberElement: null,
      cardNumberValid: false,
      cardExpiryElement: null,
      cardExpiryValid: false,
      cardCVCElement: null,
      cardCVCValid: false,
      cardPostalCodeElement: null,
      cardPostalCodeValid: false,
    };
  },
  methods: {
    brandLogo(brand) {
      return require(`@/assets/images/cc-${brand}.png`);
    },
    validateInput(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    isExpired(card) {
      let now = DateTime.utc();
      let expirationDate = DateTime.fromISO(card.expirationDate);
      let dt = now.diff(expirationDate);
      return dt.toMillis() >= 0;
    },
    getJWT() {
      let customer = sessionStorage.getItem("customerInfo");
      if (customer) {
        return JSON.parse(customer).token.token;
      } else {
        throw new Error("Must be logged in");
      }
    },
    fetchCustomerBillingInfo() {
      var url = `${BACKEND_URL.Base}/customers/${this.customerInfo.user.id}/billing-info`;
      let jwt = this.getJWT();

      let reqHeaders = { "Content-type": "application/json" };
      if (jwt) {
        reqHeaders["Authorization"] = jwt;
      }
      fetch(url, {
        mode: "cors",
        method: "get",
        headers: reqHeaders,
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            throw new Error(
              `error fetching ${url}: http status ${response.status} ${response.statusText}`
            );
          }
        })
        .then((info) => {
          this.form = info;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchCustomerCards() {
      var url = `${BACKEND_URL.Base}/customers/${this.customerInfo.user.id}/payment-methods`;
      let jwt = this.getJWT();

      let reqHeaders = { "Content-type": "application/json" };
      if (jwt) {
        reqHeaders["Authorization"] = jwt;
      }
      fetch(url, {
        mode: "cors",
        method: "get",
        headers: reqHeaders,
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            throw new Error(
              `error fetching ${url}: http status ${response.status} ${response.statusText}`
            );
          }
        })
        .then((paymentMethods) => {
          this.cards = paymentMethods;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    deleteCard(card) {
      card.isActive = false;
      console.log("Deactivating card " + JSON.stringify(card));
      this.saveCard(card);
    },
    setPreferred(card) {
      console.log("Setting card as preferred " + JSON.stringify(card));
      card.isDefault = true;
      this.saveCard(card);
    },
    saveCard(card) {
      let url = `${BACKEND_URL.Base}/customers/${this.customerInfo.user.id}/card`;
      let jwt = this.customerInfo.token.token;
      this.processing = true;

      fetch(url, {
        method: "put",
        mode: "cors",
        headers: {
          Authorization: jwt,
          "Content-type": "application/json",
        },
        body: JSON.stringify(card),
      })
        .then((response) => {
          if (response.ok) {
            this.fetchCustomerCards();
            return;
          } else {
            throw new Error(
              `Error saving card ${response.status} ${response.statusText}`
            );
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    submitBillingInfo() {
      this.$v.form.$touch();
      if (!this.validForm) {
        return;
      }

      let jsonBody = JSON.stringify(this.form);

      let url = `${BACKEND_URL.Base}/customers/${this.customerInfo.user.id}/billing-info`;
      let jwt = this.customerInfo.token.token;

      this.processing = true;

      fetch(url, {
        method: "post",
        mode: "cors",
        headers: {
          Authorization: jwt,
          "Content-type": "application/json",
        },
        body: jsonBody,
      })
        .then((response) => {
          if (response.ok) {
            return null;
          } else {
            return response.text();
          }
        })
        .then(resultIfError => {
            if(resultIfError) {
              this.billingInfoError = resultIfError;
              this.$bvToast.show("failure-toast");
            } else {
              this.processing = false;
              this.$bvToast.show("success-toast");

              setTimeout(() => {
                this.$bvToast.hide("success-toast");
              }, 1250);
            }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    showCardSetupModal() {
      this.cardSetupSuccessful = false;
      this.processing = false;
      this.$refs.cardSetupErrorBox.hide();
      this.createAndMountStripeElements();
      this.$refs.cardSetupModal.show();
    },
    startCardSetup() {
      this.$refs.cardSetupErrorBox.hide();
      this.processing = true;
      let url = `${BACKEND_URL.Base}/customers/${this.customerInfo.user.id}/setup-card?isDefault=true`;
      let jwt = `${this.customerInfo.token.token}`;

      fetch(url, {
        method: "post",
        mode: "cors",
        headers: {
          Authorization: jwt,
          Accept: "application/json",
        },
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            response.text().then((text) => {
              throw new Error(response.status + " " + text);
            });
          }
        })
        .then((json) => {
          this.stripe
            .confirmCardSetup(json.clientSecret, {
              payment_method: {
                card: this.cardNumberElement,
              },
            })
            .then((result) => {
              this.processing = false;
              if (result.error) {
                console.error(result.error.message);
                this.cardSetupError = result.error;
                this.cardSetupSuccessful = false;
                this.$bvToast.show("cardSetupErrorBox");
              } else {
                this.cardSetupSuccessful = true;
                setTimeout(() => {
                  this.$refs.cardSetupModal.hide();
                  this.fetchCustomerCards();
                }, 1500);
              }
            });
        })
        .catch((error) => {
          console.error(error);
        });
    },
    createAndMountStripeElements() {
      const style = {
        base: {
          fontSize: "18px",
          color: "#495057",
          fontFamily: "Roboto Sans,sans-serif",
          "::placeholder": {
            color: "#aab7c4",
          },
        },
        invalid: {
          color: "#fa755a",
          iconColor: "#fa755a",
        },
      };

      try {
        var elements = this.stripe.elements();

        this.cardNumberElement = elements.create("cardNumber", {
          style: style,
          placeholder: "Card Number",
        });
        this.cardNumberElement.mount("#card-number-element");

        this.cardNumberElement.on(
          "change",
          this.errorDisplayFor("cardNumberValid", this.$refs.cardErrors)
        );

        this.cardExpiryElement = elements.create("cardExpiry", {
          style: style,
        });
        this.cardExpiryElement.mount("#card-expiry-element");
        this.cardExpiryElement.on(
          "change",
          this.errorDisplayFor("cardExpiryValid", this.$refs.cardExpiryErrors)
        );

        this.cardCvcElement = elements.create("cardCvc", {
          style: style,
        });
        this.cardCvcElement.mount("#card-cvc-element");
        this.cardCvcElement.on(
          "change",
          this.errorDisplayFor("cardCVCValid", this.$refs.cardCvcErrors)
        );

        this.cardPostalCodeElement = elements.create("postalCode", {
          style: style,
          placeholder: "Zip Code (from your billing address)",
        });
        this.cardPostalCodeElement.mount("#card-postalCode-element");
        this.cardPostalCodeElement.on(
          "change",
          this.errorDisplayFor(
            "cardPostalCodeValid",
            this.$refs.cardPostalCodeErrors
          )
        );
        console.log("Mounted stripe elements inputs");
      } catch (error) {
        console.error(error);
      }
    },
    errorDisplayFor(validity, elem) {
      let v = this;
      return (event) => {
        if (event.error) {
          elem.textContent = event.error.message;
        } else {
          elem.textContent = "";
        }
        v[validity] = event.complete;
      };
    },
  },
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(1),
      },
      company: {},
      email: {
        required,
        email,
      },
      address1: {
        required,
        minLength: minLength(2),
      },
      zip: {
        required,
        minLength: minLength(4),
      },
      address2: {
        requiredIf,
      },
      city: {
        required,
        minLength: minLength(2),
      },
      country: {
        required,
        minLength: minLength(2),
      },
      taxNumber: {
        minLength: minLength(5),
      },
    },
  },
};
</script>

<style lang="scss">
@font-face {
  font-family: OpenSans;
  src: url(../../../assets/fonts/OpenSans-Light.ttf);
  font-weight: 200;
}

@font-face {
  font-family: OpenSans;
  src: url(../../../assets/fonts/OpenSans-Regular.ttf);
  font-weight: 400;
}

@font-face {
  font-family: OpenSans;
  src: url(../../../assets/fonts/OpenSans-Bold.ttf);
  font-weight: 600;
}
// Global Settings
$font-family: "OpenSans";
$font-bold: 700;

.container-internal {
  max-width: 935px;
  margin: initial;
  padding: 50px 0;
  padding-left: 7%;
  height: 100%;
  h2 {
    font-size: 30px;
    font-weight: 700;
    margin-bottom: 35px;
  }
}

// Form Settings
$border-input: 1px solid #f7f7f7;
$bg-input: #f7f7f7;
$color-input: #000000;
$font-size-input: 18px;
$color: #000000;
$line-height-input: 23px;
$padding-input: 15px 18px 15px 55px;
$link-color: #68a432;
// Icon Settings
$icon-color: #999999;

h3 {
  border-top: 1px solid lightgray;
  margin-top: 1rem;
}

.card-label {
  text-align: center;
  width: 100%;
}
.small-button {
  padding: 0.4rem 1rem;
  margin: 0;
  float: right;
}
.badge-button {
  opacity: 0.6;
}
.badge-button:hover {
  opacity: 1;
  background-color: red;
}

.img-payment {
  margin-bottom: 20px;
  max-width: 254px;
  width: 100%;
}

.paymentInput {
  background: #f7f7f7;
  border: 1px solid rgb(219, 219, 219); // #f7f7f7;
  margin-bottom: 0.5em;
  border-radius: 6px;
  .StripeElement--focus {
    outline: none;
    box-shadow: none;
  }
  &.input-group {
    svg {
      font-size: 20px;
      path {
        fill: #999999;
      }
    }
  }
  .input-group-text {
    background: transparent;
    border: 0;
    line-height: 41px;
    width: 57px;
  }
  .form-control {
    background: #f7f7f7 !important;
    border: 0;
    height: auto;
    line-height: 41px;
    align-self: center;
  }
  .card-label {
    text-align: center;
    width: 100%;
  }
}

.twoColumn {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
}
//STRIPE ELEMENTS
.stripe-element-container {
  align-items: center;
  background: #f7f7f7 !important;
  display: grid;
}
.StripeElement--focus {
  box-shadow: 0;
}

/* Padding for Stripe Element containers */
.stripe-element-container {
  padding-top: 0.55rem;
  padding-bottom: 0.5rem;
}

/* Blue outline on focus */
.StripeElement--focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

/* Can't see what I type without this */
#card-number.form-control,
#card-cvc.form-control,
#card-exp.form-control {
  display: inline-block;
}
.sr-field-error {
  color: tomato;
}
.spinner,
.spinner:before,
.spinner:after {
  border-radius: 50%;
}
.spinner {
  color: maroon;
  font-size: 22px;
  text-indent: -99999px;
  margin: 0px auto;
  position: relative;
  width: 20px;
  height: 20px;
  box-shadow: inset 0 0 0 2px;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
}
.spinner:before,
.spinner:after {
  position: absolute;
  content: "";
}
.spinner:before {
  width: 10.4px;
  height: 20.4px;
  background: var(--accent-color);
  border-radius: 20.4px 0 0 20.4px;
  top: -0.2px;
  left: -0.2px;
  -webkit-transform-origin: 10.4px 10.2px;
  transform-origin: 10.4px 10.2px;
  -webkit-animation: loading 2s infinite ease 1.5s;
  animation: loading 2s infinite ease 1.5s;
}
.spinner:after {
  width: 10.4px;
  height: 10.2px;
  background: var(--accent-color);
  border-radius: 0 10.2px 10.2px 0;
  top: -0.1px;
  left: 10.2px;
  -webkit-transform-origin: 0px 10.2px;
  transform-origin: 0px 10.2px;
  -webkit-animation: loading 2s infinite ease;
  animation: loading 2s infinite ease;
}

@-webkit-keyframes loading {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@keyframes loading {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
.card-label {
  width: 8ch;
  text-align: right;
  font-family: "OpenSans";
}

form {
  font-family: $font-family;
  font-size: $font-size-input;
  margin: auto;
  max-width: 415px;
  width: 100%;
  .input-group {
    margin: 0 0 8px 0;
    .formIcons {
      position: absolute;
      top: 1px;
      left: 1px;
      width: 57px;
      line-height: 53px;
      z-index: 100;
      svg {
        font-size: 16px;
        position: relative;
        z-index: 100;
        width: 57px;
        path {
          fill: $icon-color;
        }
      }
      &.email,
      &.password,
      &.coupon,
      &.creditcard {
        svg {
          font-size: 20px;
        }
      }
    }
    input {
      background: $bg-input;
      background-image: none !important;
      border: $border-input;
      border-radius: 6px !important;
      color: $color-input;
      font-size: $font-size-input;
      height: auto;
      line-height: $line-height-input;
      padding: $padding-input;
      &:focus {
        background-color: #ffffff;
        border: 1px solid #dcdcdc;
      }
    }

    .form-control {
      &:focus,
      .is-invalid,
      .is-valid {
        box-shadow: none;
      }
    }
    .invalid-feedback-custom {
      color: darkred;
    }
  }

  .b-toast {
    width: 100%;
    max-width: 100%;
  }

  #cardSetupErrorBox {
    width: 100%;
    max-width: 100%;
    border: 0;
    background-color: #ffebeb;
    border-radius: 0;
    box-shadow: none;
    text-align: center;
    font-size: 18px;
    color: #ff0000;
    line-height: 24px;
    padding: 10px 15px;
  }
}
</style>
